import { useReactiveVar } from "@apollo/client";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import React, { useEffect, useState } from "react";
import { AlertCircle } from "react-feather";
import { Helmet } from "react-helmet-async";
import { Trans } from "react-i18next";
import { RouteComponentProps } from "react-router";

import { auth, scheduling } from "/apollo/client/local";
import { useAppointmentsIndex } from "/b2c/query/appointments.b2c";
import InfoBanner from "/component/base/InfoBanner/InfoBanner";
import InlineMessage from "/component/base/InlineMessage";
import Link from "/component/base/Link";
import Text from "/component/base/Text";
import MainContent from "/component/layout/MainContent";
import AddressModal from "/component/modal/AddressModal";
import AthenaBillingLearnMoreModal from "/component/modal/AthenaBillingLearnMoreModal";
import OnboardingModal from "/component/modal/OnboardingModal";
import SetAddress from "/component/partial/SetAddress";
import routes from "/constant/url.constant";
import { useCedarPendingInvoiceCount, useInfoBanners, useTranslation } from "/hook";
import { layout } from "/styles";
import { reactPlugin } from "/util/appInsights.util";
import { AnalyticsUserFlow } from "/util/firebase.util";

import { Divider, HeaderContainer } from "./Dashboard.styles";
import DashboardLinks from "./DashboardLinks";
import SystemMessageModal from "./SystemMessageModal";
import UpcomingVisits from "./UpcomingVisits";
import useDashboard from "./useDashboard";

const HomePage = ({ history }: RouteComponentProps) => {
  const { t } = useTranslation("home");
  const { data, loading } = useDashboard();
  const { data: appointmentsData, loading: appointmentsLoading } = useAppointmentsIndex({
    showPast: false,
  });
  const { isAuthenticated } = useReactiveVar(auth.var);
  const { pendingInvoiceCount } = useCedarPendingInvoiceCount();
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [learnMoreModalOpen, setLearnMoreModalOpen] = useState(false);
  const [onboardingModalOpen, setOnboardingModalOpen] = useState(false);
  const { enabled, showIcon, messageText, linkText, link, variant } = useInfoBanners("home");

  useEffect(() => {
    scheduling.updateAnalytics(AnalyticsUserFlow.SCHEDULE_VISIT);
  }, []);

  const hasCmdFacilities = !!data?.cmdFacilities?.length;

  const patient = appointmentsData?.patient;

  return (
    <>
      {isAuthenticated && <SystemMessageModal />}
      <MainContent
        pageTitle={t("headline")}
        headerAccessory={
          <HeaderContainer>
            <SetAddress buttonStyle="blank" />
          </HeaderContainer>
        }
      >
        <Helmet>
          <title>{t("title")}</title>
        </Helmet>

        <InfoBanner
          enabled={enabled}
          showIcon={showIcon}
          linkText={linkText}
          link={link}
          variant={variant}
          css={{ marginBottom: 24 }}
        >
          <Text variant="body1">{messageText}</Text>
        </InfoBanner>

        {!!pendingInvoiceCount && (
          <InlineMessage
            css={layout.margin("skip", "skip", "expanded", "skip")}
            icon={<AlertCircle />}
            variant="important"
          >
            <Text>
              <Trans
                components={{ invoicesLink: <Link to={"account/billing-accounts"} /> }}
                i18nKey="invoiceReady"
                t={t}
                values={{ count: pendingInvoiceCount }}
              />
            </Text>
          </InlineMessage>
        )}

        <DashboardLinks
          onLinkClick={(link) => {
            if (link === routes.telehealthIntro && !isAuthenticated) {
              setOnboardingModalOpen(true);
            } else {
              history.push(link);
            }
          }}
          hasCmdFacilities={hasCmdFacilities}
          isLoading={loading}
        />
        <Divider css={layout.margin(40, "skip")} />
        <UpcomingVisits data={patient} isLoading={appointmentsLoading && !patient} />
        <AddressModal isOpen={addressModalOpen} close={() => setAddressModalOpen(false)} />
        <AthenaBillingLearnMoreModal
          isOpen={learnMoreModalOpen}
          close={() => setLearnMoreModalOpen(false)}
        />
        {onboardingModalOpen && <OnboardingModal onClose={() => setOnboardingModalOpen(false)} />}
      </MainContent>
    </>
  );
};

export default withAITracking(reactPlugin, HomePage, undefined, "ai-tracking");
